/**
 * OpenCodeTuiAdapter — drives the `opencode` TUI (https://opencode.ai) as a
 * raw relayed terminal. v1 declares NO structured events (no trusted
 * transcript/hook source is wired), no prefill, no passive turns, and no
 * resume (--continue behavior on an empty state dir is unverified; enable
 * after live verification). Auth is env-based: the bootstrap bundle delivers
 * CONVEYOR_AGENT_KEY/_PROVIDER/_MODEL and buildSpawn maps them to the
 * provider-native env var so the key never needs an on-disk auth file.
 */
import { buildPromptBytes, inheritedEnv } from "../pty-support.js";
import { cleanTerminalOutput } from "../spawn-args.js";
import {
  findOnPath,
  TuiUnavailableError,
  type TuiAdapter,
  type TuiCapabilities,
  type TuiFingerprintInput,
  type TuiSpawnInput,
  type TuiSpawnSpec,
} from "./types.js";

/** provider → the env var its SDK/CLI reads natively. */
const PROVIDER_KEY_ENV: Record<string, string> = {
  openai: "OPENAI_API_KEY",
  anthropic: "ANTHROPIC_API_KEY",
};

export class OpenCodeTuiAdapter implements TuiAdapter {
  readonly id = "opencode" as const;
  readonly capabilities: TuiCapabilities = {
    resume: false,
    structuredEvents: false,
    prefill: false,
    passiveTurns: false,
  };

  constructor(private readonly env: NodeJS.ProcessEnv = process.env) {}

  resolveBinary(env: NodeJS.ProcessEnv = this.env): string {
    const override = env.CONVEYOR_OPENCODE_BIN;
    const found = override ? findOnPath(override, env) : findOnPath("opencode", env);
    if (!found) {
      throw new TuiUnavailableError(
        "opencode",
        "The opencode CLI is not available in this environment. It must be baked " +
          "into the pod image (see Dockerfile.base) — re-run Build Image for this " +
          "project, or set CONVEYOR_OPENCODE_BIN to its absolute path.",
      );
    }
    return found;
  }

  buildSpawn(input: TuiSpawnInput): TuiSpawnSpec {
    const env = { ...inheritedEnv() };
    delete env.CONVEYOR_AGENT_KEY;
    const key = this.env.CONVEYOR_AGENT_KEY;
    const provider = this.env.CONVEYOR_AGENT_PROVIDER ?? "openai";
    const keyEnvVar = PROVIDER_KEY_ENV[provider];
    if (key && keyEnvVar) env[keyEnvVar] = key;

    const model = this.env.CONVEYOR_AGENT_MODEL ?? input.options.model;
    const args: string[] = [];
    if (model) args.push("--model", model.includes("/") ? model : `${provider}/${model}`);
    return { file: this.resolveBinary(), args, env };
  }

  async prepareEnvironment(): Promise<void> {
    // Env-based auth: nothing to materialize. Session state persistence is the
    // entrypoint's job (GCS-FUSE symlinks for ~/.local/share/opencode).
  }

  spawnFingerprint(input: TuiFingerprintInput): string {
    return JSON.stringify(["opencode", input.model, input.cwd]);
  }

  encodePromptBytes(text: string): string {
    // Bracketed paste — standard for modern TUIs; revisit if live verification
    // shows opencode's input mishandles it.
    return buildPromptBytes(text);
  }

  buildExitErrors(exitCode: number, rawOutput: string): string[] {
    const errors = [`opencode exited (code ${exitCode}) without a result`];
    const tail = cleanTerminalOutput(rawOutput);
    if (tail) errors.push(`Last terminal output before exit:\n${tail}`);
    return errors;
  }
}
